import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { ButtonSizes, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { useManifestTableTracking } from '../../../hooks/useManifestTableTracking'
import { useManifestContext } from '../ManifestContext'
import { ControlButton } from '../ui'
import { ControlSelect } from './ControlSelect'
import { useManifestFilterConfigs } from './useManifestFilterConfigs'

const Bar = styled.div`
  align-items: center;
  background-color: ${Colors.Semantic.Neutral.light_grey};
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
`

const Label = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  font-weight: 600;
  white-space: nowrap;
`

export const ManifestFiltersBar = () => {
  const { availableFilters, filters, listing, setFilters } =
    useManifestContext()
  const { onManifestFilterReset, onManifestFiltersApplied, onManifestReset } =
    useManifestTableTracking(listing)
  const filterConfigs = useManifestFilterConfigs(availableFilters)

  const hasActiveFilters = Object.values(filters).some((v) => v.length > 0)

  const handleFilterChange = (filterKey: string, value: string) => {
    const selectedItem = filterConfigs
      .find((c) => c.filterKey === filterKey)
      ?.items.find((i) => i.value === value)
    const updated = value
      ? { ...filters, [filterKey]: selectedItem?.apiValues ?? [value] }
      : { ...filters, [filterKey]: [] }
    setFilters(updated)
    onManifestFiltersApplied({
      filterName: value || 'all',
      filterTitle: filterKey,
      isSelected: !!value,
    })
    if (!value) {
      onManifestFilterReset({ filterKey, filterTitle: filterKey })
    }
  }

  const clearAllFilters = () => {
    setFilters({})
    onManifestReset()
  }

  if (filterConfigs.length === 0)
    return (
      <Bar>
        <Label>
          <FormattedMessage id="Listing.manifestAgGrid.filterBar.noFilters" />
        </Label>
      </Bar>
    )

  return (
    <Bar>
      <Label>
        <FormattedMessage id="Listing.manifestAgGrid.filterBar.filterBy" />
      </Label>
      {filterConfigs.map(({ filterKey, items, title }) => {
        const appliedValues = filters[filterKey] ?? []
        // An option may apply several raw values (a top-level Category covers
        // its subcategories), so resolve the selected option by membership.
        const selectedItem = appliedValues.length
          ? items.find((i) =>
              appliedValues.some((v) => i.apiValues.includes(v))
            )
          : undefined
        const selectedValue = selectedItem?.value ?? appliedValues[0] ?? ''
        const selectedLabel = selectedValue
          ? (selectedItem?.label ?? selectedValue)
          : title

        return (
          <ControlSelect
            displayValue={selectedLabel}
            key={filterKey}
            onChange={(e) => handleFilterChange(filterKey, e.target.value)}
            value={selectedValue}
          >
            <option value="">{title}</option>
            {items.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </ControlSelect>
        )
      })}
      {hasActiveFilters && (
        <ControlButton
          appearance="secondary"
          onClick={clearAllFilters}
          size={ButtonSizes.small}
        >
          <FontAwesomeIcon icon={faXmark} />
          <FormattedMessage id="Listing.manifestAgGrid.filterBar.clear" />
        </ControlButton>
      )}
    </Bar>
  )
}
